import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";

interface TodoItemProps {
    id: number;
    description: string;
    completed: boolean;
    disabled?: boolean;
    isLoading?: boolean;
    onSave: (status: number) => Promise<void>;
}

export const TodoItem: React.FC<TodoItemProps> = ({
    description,
    completed,
    disabled = false,
    isLoading = false,
    onSave,
}) => {

    const [isSaving, setIsSaving] = useState(false);
    const [draftChecked, setDraftChecked] = useState(completed);

    const isDirty = draftChecked !== completed && !isSaving;

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isSaving) return;
        setDraftChecked(event.target.checked);
    };

    const handleCancel = () => {
        setDraftChecked(completed);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try{
            await onSave(draftChecked ? 1 : 0);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        setDraftChecked(completed);
    }, [completed]);

    const isInteractionDisabled = disabled || isSaving || isLoading;
    
    return (
        <Box
            borderColor = {"divider"}
            borderRadius= {1}
            px= {1}
            py= {1.5}
            sx={{
                borderWidth: "1px",
                borderStyle: "solid",
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <Checkbox
                    checked={draftChecked}
                    onChange={handleCheckboxChange}
                    disabled={isInteractionDisabled}
                />
                <Typography
                    variant="body1"
                    sx={{
                        color: draftChecked ? "text.secondary" : "text.primary",
                    }}
                >
                    {description}
                </Typography>
                {isSaving && (
                    <CircularProgress size={14} sx={{ ml: "auto", flexShrink: 0 }} />
                )}
            </Box>
            { isDirty && (
                <>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button
                            onClick={handleCancel}
                            size="small"
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isSaving}
                            size="small"
                            variant="contained"
                        >
                            {
                                isLoading ? (
                                    <CircularProgress size={16} color="inherit"/>
                                ) : "Update"
                            }
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};